/**
 * Conversion Tracking Utility
 * Tracks all critical user actions for analytics and optimization
 */

import { safeLocalGet } from '@/utils/safeStorage';

export const ANALYTICS_CONSENT_KEY = 'resumeready_optional_analytics_v1';

export const hasAnalyticsConsent = () => (
  safeLocalGet(ANALYTICS_CONSENT_KEY) === 'granted'
);

export const createEventId = (prefix = 'event') => {
  try {
    if (window.crypto?.randomUUID) return `${prefix}-${window.crypto.randomUUID()}`;
    if (window.crypto?.getRandomValues) {
      const bytes = new Uint32Array(4);
      window.crypto.getRandomValues(bytes);
      return `${prefix}-${Array.from(bytes, (part) => part.toString(16)).join('-')}`;
    }
  } catch {
    // Safari can deny crypto access in strict privacy contexts.
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

// Track conversion events
export const trackEvent = (eventName, properties = {}) => {
  if (typeof window !== 'undefined' && window.trackConversion) {
    return window.trackConversion(eventName, properties);
  }
  return null;
};

// Predefined conversion events
export const ConversionEvents = {
  // Homepage funnel
  HOMEPAGE_VIEWED: 'homepage_viewed',
  RESUME_SCANNER_OPENED: 'resume_scanner_opened',
  RESUME_UPLOADED: 'resume_uploaded',
  SCAN_RESULTS_VIEWED: 'scan_results_viewed',
  SIGNUP_FROM_SCANNER: 'signup_from_scanner',
  
  // Auth funnel
  SIGNUP_STARTED: 'signup_started',
  SIGNUP_COMPLETED: 'signup_completed',
  LOGIN_COMPLETED: 'login_completed',
  GOOGLE_OAUTH_CLICKED: 'google_oauth_clicked',
  
  // Premium feature interest
  PREMIUM_FEATURE_CLICKED: 'premium_feature_clicked',
  UPGRADE_MODAL_VIEWED: 'upgrade_modal_viewed',
  UPGRADE_CLICKED: 'upgrade_clicked',
  PRICING_PAGE_VIEWED: 'pricing_page_viewed',
  
  // Feature usage
  RESUME_CREATED: 'resume_created',
  RESUME_DOWNLOADED: 'resume_downloaded',
  JOB_TAILOR_USED: 'job_tailor_used',
  MOCK_INTERVIEW_STARTED: 'mock_interview_started',
  JOB_SAVED: 'job_saved',
  
  // Conversion goals
  SUBSCRIPTION_STARTED: 'subscription_started',
  PAYMENT_COMPLETED: 'payment_completed',
  CREDITS_PURCHASED: 'credits_purchased',
};

// Track page views
export const trackPageView = (pageName, properties = {}) => {
  trackEvent('page_view', {
    page_name: pageName,
    ...properties
  });
};

// Track premium feature gating
export const trackPremiumGate = (featureName, planRequired) => {
  trackEvent(ConversionEvents.PREMIUM_FEATURE_CLICKED, {
    feature: featureName,
    plan_required: planRequired,
    has_subscription: false // Will be updated dynamically
  });
};

// Track conversion funnel step
export const trackFunnelStep = (funnelName, stepName, stepNumber) => {
  trackEvent('funnel_step', {
    funnel: funnelName,
    step: stepName,
    step_number: stepNumber
  });
};

export default {
  trackEvent,
  trackPageView,
  trackPremiumGate,
  trackFunnelStep,
  ConversionEvents
};
